// src/pages/ResetPassword.jsx
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// même logique que dans Auth.jsx
const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:5000").replace(/\/+$/, "");
const API_URL = `${API_BASE}/api`;

function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
}

export default function ResetPassword() {
    const query = useQuery();
    const navigate = useNavigate();

    const initialEmail = query.get("email") || "";
    const token = query.get("token") || "";

    const [email] = useState(initialEmail); // on ne laisse pas modifier l’email
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);

    // Si pas de token ou pas d’email dans l’URL -> message d’erreur
    if (!token || !email) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md text-center">
                    <h1 className="text-xl font-semibold mb-4 text-red-600">
                        Lien invalide
                    </h1>
                    <p className="text-sm text-gray-600 mb-6">
                        Le lien de réinitialisation semble incomplet ou invalide.
                    </p>
                    <button
                        className="px-4 py-2 rounded-full bg-sky-700 text-white text-sm font-medium"
                        onClick={() => navigate("/login")}
                    >
                        Retour à la connexion
                    </button>
                </div>
            </div>
        );
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!password || !confirm) {
            toast.warning("Merci de saisir et confirmer le nouveau mot de passe.");
            return;
        }
        if (password !== confirm) {
            toast.warning("Les mots de passe ne correspondent pas.");
            return;
        }
        if (password.length < 10) {
            toast.warning("Le mot de passe doit faire au moins 10 caractères.");
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/auth/reset-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email,
                    token,
                    new_password: password,
                }),
            });

            const data = await res.json();

            if (res.ok) {
                toast.success(
                    data.message || "Mot de passe mis à jour avec succès. Vous pouvez vous reconnecter."
                );
                navigate("/login");
            } else {
                toast.error(data.message || "Impossible de réinitialiser le mot de passe.");
            }
        } catch (err) {
            console.error("reset-password error:", err);
            toast.error("Erreur serveur.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md">
                <h1 className="text-2xl font-bold text-center text-sky-700 mb-2">
                    Réinitialiser votre mot de passe
                </h1>
                <p className="text-xs text-gray-600 mb-6 text-center">
                    Choisissez un nouveau mot de passe pour votre compte FlowDevis.
                </p>

                <div className="mb-4 text-xs text-gray-500">
                    <div className="font-medium">Compte :</div>
                    <div className="text-gray-700 break-all">{email}</div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <input
                        type="password"
                        placeholder="Nouveau mot de passe"
                        className="w-full p-3 border rounded"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="new-password"
                    />
                    <input
                        type="password"
                        placeholder="Confirmer le mot de passe"
                        className="w-full p-3 border rounded"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        autoComplete="new-password"
                    />
                    <p className="text-xs text-gray-500">
                        Minimum 10 caractères, idéalement un mélange de lettres, chiffres et symboles.
                    </p>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-sky-700 text-white p-3 rounded-full text-sm font-medium hover:bg-sky-600 disabled:opacity-60"
                    >
                        {loading ? "Réinitialisation…" : "Valider le nouveau mot de passe"}
                    </button>

                    <div className="text-center mt-4">
                        <button
                            type="button"
                            onClick={() => navigate("/login")}
                            className="text-xs text-gray-600 hover:underline"
                        >
                            Retour à la page de connexion
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
